import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  isStayPartnerType, accentColorForType, PARTNER_TYPES,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Button, Chip, Grid, Avatar, Divider, Paper, IconButton,
  TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CircularLoading from '../components/CircularLoading';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';

export default function PartnerDetail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const partnerdata = useSelector((s) => s.partnerdata);
  const inventorydata = useSelector((s) => s.inventorydata);
  const auth = useSelector((s) => s.auth);

  const { fetchPartners, fetchPartnerById, fetchInventory } = api;
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    if (!partnerdata.partners) dispatch(fetchPartners());
    dispatch(fetchInventory(partnerId));
    dispatch(fetchPartnerById(partnerId)).then((p) => p && setPartner(p));
  }, [dispatch, fetchPartners, fetchPartnerById, fetchInventory, partnerId]);

  useEffect(() => {
    if (!partner && partnerdata.partners) {
      const found = partnerdata.partners.find((p) => p.id === partnerId);
      if (found) setPartner(found);
    }
  }, [partnerdata.partners, partner, partnerId]);

  const inventory = inventorydata.byPartnerId?.[partnerId] || [];
  const fromPrice = useMemo(() => {
    const prices = inventory
      .filter((i) => i.active !== false)
      .map((i) => Number(i.price) || 0)
      .filter((n) => n > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [inventory]);

  if (!partner) return <CircularLoading />;
  const accent = accentColorForType(partner.partnerType);
  const galleryUrls = Object.values(partner.images?.gallery || {});

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/partners')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          {partner.businessName}
        </Typography>
        <Chip
          label={partner.partnerType}
          sx={{ backgroundColor: `${accent}1a`, color: accent, fontWeight: 600 }}
        />
      </Stack>

      <Box sx={{
        display: 'grid', gap: 1.5, mb: 3,
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
        gridTemplateRows: { xs: 'auto', md: '180px 180px' },
      }}>
        <Box sx={{
          gridRow: { md: '1 / span 2' },
          aspectRatio: { xs: '4/3', md: 'unset' },
          borderRadius: 4,
          backgroundColor: '#F3F4F6',
          backgroundImage: partner.images?.cover ? `url(${partner.images.cover})` : 'none',
          backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{
            display: { xs: 'none', md: 'block' },
            borderRadius: 4,
            backgroundColor: '#F3F4F6',
            backgroundImage: galleryUrls[i] ? `url(${galleryUrls[i]})` : 'none',
            backgroundSize: 'cover', backgroundPosition: 'center',
          }} />
        ))}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StarIcon sx={{ color: '#111', fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600 }}>
                {partner.rating?.avg ? partner.rating.avg.toFixed(1) : '—'}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                ({partner.rating?.count || 0})
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
              <LocationOnIcon fontSize="small" />
              <Typography variant="body2">
                {[partner.address?.line1, partner.address?.city].filter(Boolean).join(', ')}
              </Typography>
            </Stack>
          </Stack>

          {partner.description ? (
            <Typography sx={{ mb: 2 }}>{partner.description}</Typography>
          ) : null}

          {isStayPartnerType(partner.partnerType) && partner.amenities ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                {t('amenities') || 'Comodidades'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ my: 1 }}>
                {Object.entries(partner.amenities)
                  .filter(([, v]) => !!v)
                  .map(([k]) => (
                    <Chip key={k} label={t(k) || k} size="small" sx={{ mb: 1 }} />
                  ))}
              </Stack>
            </>
          ) : null}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            {isStayPartnerType(partner.partnerType)
              ? (t('rooms') || 'Habitaciones')
              : (t('products') || 'Productos')}
          </Typography>

          <Stack spacing={1.5}>
            {inventory
              .filter((i) => i.active !== false)
              .map((item) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      variant="rounded"
                      src={item.images && Object.values(item.images)[0]}
                      sx={{ width: 80, height: 60 }}
                    >
                      {(item.name || '?').slice(0, 1)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isStayPartnerType(partner.partnerType)
                          ? `${item.capacity?.adults || 1} ${t('adults') || 'adultos'}`
                          : item.category || ''}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {partner.currency || ''} {Number(item.price || 0).toFixed(2)}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            {inventory.length === 0 && (
              <Typography color="text.secondary">
                {t('no_inventory') || 'No hay inventario disponible'}
              </Typography>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, position: 'sticky', top: 16 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {fromPrice
                ? `${t('from') || 'Desde'} ${partner.currency || ''} ${fromPrice.toFixed(2)}`
                : (t('contact_for_price') || 'Consultar precio')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isStayPartnerType(partner.partnerType)
                ? (t('per_night') || 'por noche')
                : (t('per_unit') || 'por unidad')}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 2, backgroundColor: MAIN_COLOR, borderRadius: 999 }}
              disabled={!auth?.profile?.uid || auth.profile.usertype !== 'customer'}
              onClick={() => navigate(`/partners/${partner.id}/checkout`)}
            >
              {t('book_now') || 'Reservar'}
            </Button>
            {auth?.profile?.usertype !== 'customer' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('login_as_customer') || 'Inicia sesión como cliente para reservar.'}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
